import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

const buttonVariants = cva(
  'inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 border font-ui font-medium transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-parchment disabled:cursor-not-allowed disabled:opacity-45 active:translate-y-px select-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'border-ink bg-ink text-parchment shadow-none hover:bg-graphite hover:border-graphite',
        secondary:
          'border-hairline bg-paper text-content-secondary shadow-none hover:border-ink hover:bg-stone hover:text-content-primary',
        danger:
          'border-state-critical bg-paper text-state-critical shadow-none hover:bg-state-critical-soft hover:border-state-critical',
        ghost:
          'border-transparent bg-transparent text-content-secondary shadow-none hover:bg-stone hover:text-content-primary',
      },
      size: {
        xs: 'px-3 py-1.5 text-sm',
        sm: 'px-4 py-2 text-base',
        md: 'px-5 py-2.5 text-base',
        lg: 'px-6 py-3 text-lg',
        xl: 'px-7 py-3.5 text-xl',
      },
      rounded: {
        sm: 'rounded-control',
        md: 'rounded-control',
        lg: 'rounded-control',
        xl: 'rounded-card',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      rounded: 'lg',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  fullWidth?: boolean;
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      loading = false,
      fullWidth = false,
      asChild = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, rounded }),
          fullWidth && 'w-full',
          loading && 'cursor-wait',
          className
        )}
        disabled={disabled || loading}
        ref={ref}
        {...props}
      >
        {loading && <Loader2 className="size-4 animate-spin opacity-70" aria-hidden="true" />}
        {children}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
